<script setup lang="ts">
import ArticleText from './ArticleText.vue';
import ArticleGallery from './ArticleGallery.vue';
import ArticleRelatedNews from './ArticleRelatedNews.vue';
import { formatDate } from '~/shared/utils/formatDate';
import { getAuthorName } from '~/shared/lib/getAuthorName';
import { isMaterialDTO } from '~/shared/lib/isMaterialDTO';
import { isAdMarkVisible } from '~/shared/lib/isPartnerArticle';
import { useOrigin } from '~/shared/lib/useOrigin';
import {
  resizeImage,
  resizeImageAttrs,
  resizeImageSrcsetWidths,
} from '~/shared/lib/resizeImage';
import { CLIENT_NEWS_BY_AUTHOR_URL } from '~/shared/api/client/news/news.const';
import type { MaterialDTO } from '~/shared/api/client/news/news.dto';
import type { ExpertNewsDTO } from '~/shared/api/client/expert-news/expert-news.dto';

import authorAvatar from './images/authorAvatar.svg';

const props = defineProps<{
  item: MaterialDTO | ExpertNewsDTO;
}>();

const pageUrl = computed(() => `${useOrigin()}${useRoute().fullPath}`);

const categoryName = computed(() => props.item.category?.name ?? '');
const categoryLink = computed(() =>
  props.item.category ? `/category/${props.item.category.id}` : '',
);

const showAdMark = computed(() => isAdMarkVisible(props.item));
const markCompany = computed(() => props.item.mark_text || '');
const markErid = computed(() =>
  isMaterialDTO(props.item) ? props.item.mark_erid : '',
);

const heroImage = computed(() => {
  if (isMaterialDTO(props.item)) {
    return !props.item.is_gallery
      ? props.item.image?.photo.file_url
      : undefined;
  }

  // Expert opinion: its own image, otherwise the expert's photo
  return (
    props.item.image?.photo?.file_url ?? props.item.person?.photo?.file_url
  );
});

const heroImageAlt = computed(
  () => props.item.image?.description ?? props.item.title,
);

// Expert opinion: the news item it comments on
const relatedNews = computed(() =>
  !isMaterialDTO(props.item) ? (props.item.news ?? null) : null,
);

const author = computed(() =>
  isMaterialDTO(props.item) ? props.item.author : null,
);
const authorName = computed(() => getAuthorName(author.value));
const authorLink = computed(() =>
  author.value ? `${CLIENT_NEWS_BY_AUTHOR_URL}${author.value.id}` : '',
);
</script>

<template>
  <div :class="$style.root">
    <!-- Full-width header: disclaimer and hero flush together -->
    <div :class="$style.head">
      <div :class="$style.disclosure">
        <PartnerDisclosure :category-name="categoryName" />
      </div>

      <!-- Hero image with the title overlaid -->
      <div v-if="heroImage" :class="$style.hero">
        <img
          :class="$style.hero__img"
          :src="resizeImage(heroImage, { width: 1440 })"
          :srcset="
            resizeImageSrcsetWidths(heroImage, [640, 960, 1440, 1920, 2880]) ||
            undefined
          "
          sizes="100vw"
          :alt="heroImageAlt"
        />
        <div :class="$style.hero__shade" />
        <div :class="$style.hero__content">
          <div :class="$style.hero__info">
            <ButtonBack :class="$style.hero__back" />
            <NuxtLink
              v-if="categoryLink"
              :to="categoryLink"
              :class="$style.categoryLink"
            >
              {{ categoryName }}
            </NuxtLink>
            <div :class="$style.hero__date">
              {{ formatDate('fullDate', props.item.publication_date) }}
            </div>
          </div>
          <h1 :class="$style.hero__title">{{ props.item.title }}</h1>
          <div :class="[$style.socials, $style.socials_hero]">
            <div :class="$style.socials__text">Поделиться:</div>
            <div :class="$style.socials__links">
              <a
                :href="`https://telegram.me/share/url?url=${pageUrl}`"
                target="_blank"
                :class="$style.socials__link"
              >
                <Icon
                  :class="$style.socials__icon"
                  name="my-icon:i-social-tg"
                />
              </a>
              <a
                :href="`https://vk.com/share.php?url=${pageUrl}`"
                target="_blank"
                :class="$style.socials__link"
              >
                <Icon
                  :class="$style.socials__icon"
                  name="my-icon:i-social-vk"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Variant without a hero image -->
    <template v-if="!heroImage">
      <div :class="$style.info">
        <ButtonBack />
        <NuxtLink
          v-if="categoryLink"
          :to="categoryLink"
          :class="$style.categoryLink"
        >
          {{ categoryName }}
        </NuxtLink>
        <div :class="$style.info__date">
          {{ formatDate('fullDate', props.item.publication_date) }}
        </div>
      </div>

      <h1 :class="$style.title">{{ props.item.title }}</h1>

      <div :class="$style.socials">
        <div :class="$style.socials__text">Поделиться:</div>
        <div :class="$style.socials__links">
          <a
            :href="`https://telegram.me/share/url?url=${pageUrl}`"
            target="_blank"
            :class="$style.socials__link"
          >
            <Icon :class="$style.socials__icon" name="my-icon:i-social-tg" />
          </a>
          <a
            :href="`https://vk.com/share.php?url=${pageUrl}`"
            target="_blank"
            :class="$style.socials__link"
          >
            <Icon :class="$style.socials__icon" name="my-icon:i-social-vk" />
          </a>
        </div>
      </div>
    </template>

    <ArticleGallery
      v-if="
        isMaterialDTO(props.item) &&
        props.item.is_gallery &&
        props.item.gallery?.[0]
      "
      :gallery="props.item.gallery[0]"
    />

    <ArticleText
      v-if="(props.item as MaterialDTO).subject"
      :class="$style.excerpt"
      :text="(props.item as MaterialDTO).subject"
    />

    <ArticleText :class="$style.text" :text="props.item.text" />

    <div v-if="author" :class="$style.author">
      <div :class="$style.author__photoWrapper">
        <img
          :src="
            resizeImageAttrs(author.photo?.file_url || authorAvatar, {
              width: 60,
              height: 60,
              mode: 'fill',
            }).src
          "
          :srcset="
            resizeImageAttrs(author.photo?.file_url || authorAvatar, {
              width: 60,
              height: 60,
              mode: 'fill',
            }).srcset
          "
          :title="authorName"
          :alt="authorName"
          :class="$style.author__photo"
        />
      </div>
      <div :class="$style.author__meta">
        <div :class="$style.author__header">Автор</div>
        <div :class="$style.author__name">{{ authorName }}</div>
        <div :class="$style.author__position">{{ author.position }}</div>
      </div>
      <div :class="$style.author__articles">
        <div :class="$style.author__header">Читать</div>
        <NuxtLink :class="$style.author__link" :to="authorLink">
          Другие статьи автора
        </NuxtLink>
      </div>
    </div>

    <div v-if="showAdMark && (markCompany || markErid)" :class="$style.legal">
      <p :class="$style.legal__row">Реклама, 16+</p>
      <p v-if="markCompany" :class="$style.legal__row">{{ markCompany }}</p>
      <p v-if="markErid" :class="$style.legal__row">erid: {{ markErid }}</p>
    </div>

    <div :class="$style.backBar">
      <div :class="$style.backBar__back">
        <ButtonBack />
      </div>
      <NuxtLink
        v-if="categoryLink"
        :to="categoryLink"
        :class="[$style.categoryLink, $style.backBar__category]"
      >
        {{ categoryName }}
      </NuxtLink>
    </div>

    <ArticleRelatedNews
      v-if="relatedNews"
      :title="relatedNews.title"
      :subject="relatedNews.subject"
      :category="relatedNews.category?.name ?? ''"
      :category-link="`/category/${relatedNews.category?.id}`"
      :link="`/${relatedNews.type}/${relatedNews.id}`"
      :image="relatedNews.image?.photo?.file_url"
    />
  </div>
</template>

<style module lang="scss">
.root {
  --partner-bg: #f4eff0;

  display: flex;
  flex-flow: column;
  gap: var(--space-2x);
  width: 100%;
  background: var(--partner-bg);
  padding-bottom: var(--space-3x);

  // Content sits in the standard column; hero and disclaimer span full width
  > * {
    width: 100%;
    max-width: 860px;
    margin-left: auto;
    margin-right: auto;
  }

  > .head {
    max-width: none;
  }
}

// The header is outside the column: the root column's gap doesn't apply
// inside it, so the hero follows the disclaimer immediately
.head {
  display: flex;
  flex-flow: column;
}

.disclosure {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-height: 40px;
  padding: 0 var(--space-2-5x);
  background: var(--color-white);
}

.categoryLink {
  @include text_caption1_caps;
  color: var(--color-red-1);
  text-decoration: none;
}

.hero {
  position: relative;
  display: flex;
  max-height: 740px;
  overflow: hidden;
  margin-bottom: var(--space-3x);

  &__img {
    width: 100%;
    height: auto;
    max-height: 740px;
    object-fit: cover;
    display: block;
    margin: 0 !important;
  }

  &__shade {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.25) 0%,
      rgba(0, 0, 0, 0.05) 40%,
      rgba(0, 0, 0, 0.6) 100%
    );
  }

  &__content {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2x);
    width: 100%;
    max-width: 860px;
    margin: 0 auto;
    padding: 40px 0;
  }

  &__info {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    gap: var(--space-2-5x);
  }

  &__back {
    color: var(--color-white) !important;

    svg path {
      fill: var(--color-white) !important;
    }
  }

  &__date {
    @include text_caption1_caps;
    color: var(--color-white);
  }

  &__title {
    @include text_h1;
    color: var(--color-white);
    margin: 0;
  }
}

.info {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: var(--space-2-5x);
  padding-top: var(--space-2x);

  &__date {
    @include text_caption1_caps;
    color: var(--color-gray-text);
  }
}

.title {
  @include text_h1;
  color: var(--color-black-1);
  margin-top: 0;
  margin-bottom: 0;
}

.socials {
  display: flex;
  flex-flow: row nowrap;
  gap: var(--space-1-5x);
  align-items: center;

  &__text {
    @include text_caption1_caps;
    color: var(--color-gray-text);
  }

  &__links {
    display: flex;
    flex-flow: row nowrap;
    gap: var(--space-1-5x);
  }

  &__link {
    width: 24px;
    height: 24px;
  }

  &__icon {
    cursor: pointer;

    :global(g rect) {
      fill: var(--color-gray-bg);
    }
  }

  &_hero {
    .socials__text {
      color: var(--color-white);
    }
  }
}

.excerpt {
  margin-bottom: var(--space-3x);
  @include text_article_body1_medium;
}

.author {
  display: grid;
  grid-template-columns: 100px 1fr auto;
  gap: 0;
  background: var(--partner-bg);
  border-bottom: 1px solid var(--color-gray-line);
  margin-top: var(--space-3x);

  &__photoWrapper {
    padding: var(--space-2-5x);
    border-right: 1px solid var(--color-gray-line);
  }

  &__photo {
    width: 60px;
    height: 60px;
    border-radius: 50%;
  }

  &__meta {
    display: flex;
    flex-flow: column nowrap;
    gap: var(--space-0-5x);
    padding: var(--space-2-5x);
  }

  &__header {
    @include text_caption1_caps;
    color: var(--color-gray-text);
  }

  &__name {
    @include text_body1_medium;
    color: var(--color-black-1);
  }

  &__position {
    @include text_body2;
    color: var(--color-gray-text);
  }

  &__articles {
    display: flex;
    flex-flow: column nowrap;
    gap: var(--space-0-5x);
    padding: var(--space-2-5x);
    border-left: 1px solid var(--color-gray-line);
  }

  &__link {
    @include text_body1_medium;
    color: var(--color-red-1);
    cursor: pointer;
    text-decoration: none;
  }
}

.legal {
  display: flex;
  flex-direction: column;
  gap: 2px;
  // Together with the root column's gap (16px) this gives ~40px
  // to the author card and the back bar
  margin-top: var(--space-3x);
  margin-bottom: var(--space-3x);

  &__row {
    font-weight: 400;
    font-size: 10px;
    line-height: 140%;
    margin: 0;
    color: var(--color-gray-text);
    white-space: pre-line;
  }
}

.backBar {
  display: flex;
  flex-flow: row nowrap;
  align-items: stretch;
  background: var(--color-white);
  border: 1px solid var(--color-gray-line);

  &__back {
    display: flex;
    align-items: center;
    padding: var(--space-2-5x);
    border-right: 1px solid var(--color-gray-line);
  }

  &__category {
    display: flex;
    align-items: center;
    padding: var(--space-2-5x);
  }
}

@media (max-width: 1379px) {
  .root {
    > * {
      padding-left: var(--space-2-5x);
      padding-right: var(--space-2-5x);
    }

    > .head {
      padding-left: 0;
      padding-right: 0;
    }
  }

  .hero {
    &__content {
      padding: var(--space-2-5x);
    }

    &__title {
      @include text_h2;
    }
  }

  .title {
    @include text_h2;
  }
}

// On mobile the title doesn't fit over the image and gets clipped by
// overflow, so the header unfolds into normal flow like in Article.vue:
// meta/title/share above the image
@media (max-width: 767px) {
  .hero {
    flex-flow: column;
    max-height: none;
    overflow: visible;

    &__img {
      order: 1;
      max-height: none;
    }

    &__shade {
      display: none;
    }

    &__content {
      position: static;
      padding: var(--space-2x) var(--space-2-5x);
    }

    &__back {
      color: var(--color-gray-text) !important;

      svg path {
        fill: var(--color-gray-text) !important;
      }
    }

    &__date {
      color: var(--color-gray-text);
    }

    &__title {
      @include text_h3;
      color: var(--color-black-1);
    }
  }

  .socials_hero {
    .socials__text {
      color: var(--color-gray-text);
    }
  }

  .title {
    @include text_h3;
  }
}

@media (max-width: 599px) {
  .author {
    grid-template-columns: 100px 1fr;

    &__articles {
      grid-column: 1 / -1;
      border-left: none;
      border-top: 1px solid var(--color-gray-line);
    }
  }
}
</style>
